import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { routes } from "../routes";

import PlayerTournamentBottomNav from "../components/PlayerTournamentBottomNav";
import TournamentSponsorSlots from "../components/TournamentSponsorSlots";
import AmigosPanel from "../components/AmigosPanel";
import FieldHint from "../components/FieldHint";
import PlayerPendingDocumentsCard from "../components/PlayerPendingDocumentsCard";
import PlayerWhatsappGroupsButton from "../components/PlayerWhatsappGroupsButton";
import PlayerRetoActions, { PlayerRetoContact } from "../components/PlayerRetoActions";
import PlayerJornadaRetoCard from "../components/PlayerJornadaRetoCard";
import { PlayerProfileScopeSwitcher } from "../components/PlayerFamilyProfileSwitcher";
import Icon from "../components/icons";
import { useAuth } from "../auth/AuthContext";
import {
  api,
  API_BASE_URL,
  ApiError,
  type DashboardMatchBrief,
  type H2HPayload,
  type PlayerDashboardPayload,
  type PlayerDashboardRestRound,
  type PlayerDashboardTournamentCard,
  type PlayerJornadaReto,
  type PlayerJornadaRetosPayload,
  type PlayerPaymentOrder,
  type PlayerStandingsPayload,
  type StandingsRow,
} from "../lib/api";
import { scoreVocab } from "../lib/labels";
import { PLAYER_STAGE_NEXT_MATCH_MESSAGE, playerStageLocked } from "../lib/playerStage";
import { participantGroupScope } from "../lib/standingsRules";
import { isSchedulePendingForPlayer, SCHEDULE_PENDING_LABEL } from "../lib/matchScheduleStatus";
import { formatMoney } from "../lib/money";
import { makeLatestOnly } from "../lib/latestOnly";
import { playerWhatsappGroups } from "../lib/tournamentExtras";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";
import { useSignedLinkRefresh } from "../lib/useSignedLinkRefresh";
import { courtNoticeName } from "../lib/courts";
import { orderPaymentHref } from "../lib/orderPaymentLink";
import { jornadaRangeText, jornadaWindowLine, jornadaWindowParts } from "../lib/jornadaDates";
import {
  RESULT_BY_ORGANIZER_NOTE,
  retoContactLine,
  retoReportHref,
  retoRival,
  retoShowsContact,
} from "../lib/retoContacto";

function formatTournamentType(value?: string | null): string {
  if (!value) return "Tennis";
  if (value === "ladder") return "Tennis";
  if (value === "round_robin") return "Round Robin";
  if (value === "elimination") return "Eliminación";
  return value;
}

function initials(name?: string | null): string {
  const parts = (name ?? "Jugador")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "J";
  return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("");
}

export function roundBadge(match?: DashboardMatchBrief | null): string {
  // Contrato display-only (gate #136): etapa canónica del backend si existe
  // (round_display_label); si no, el round_label CRUDO exacto — "Jornada 4" o
  // "Matchups 4" NO se reconstruyen como "Semana 4".
  const stage = match?.round_display_label?.trim();
  if (stage) return stage;
  const label = match?.round_label?.trim();
  if (!label) return match?.match_number ? `Juego ${match.match_number}` : "Próximo";
  return label;
}

/** El rival: por el lado del jugador que manda el servidor (en dobles los
 * dos lados vienen sin perfil y antes salía la propia pareja). */
/** «Jornada 3 · del lunes 6 al domingo 12 de octubre» (Eduardo 2026-10-04;
 * desde el 2026-10-05 sin «juega antes del domingo»): solo en torneos sin
 * calendario de canchas y con fechas puestas por el organizador. Con
 * calendario manda el horario. */
export function nextMatchWindow(
  match: DashboardMatchBrief | null | undefined,
  stageLocked = false,
  withLabel = true,
): string | null {
  if (!match || stageLocked || match.uses_court_calendar !== false) return null;
  // Descanso: no hay nada que jugar esa jornada.
  if (match.status?.toLowerCase() === "bye") return null;
  return jornadaWindowLine(withLabel ? roundBadge(match) : null, match.round_starts_on, match.round_ends_on);
}

/** Lo mismo que `nextMatchWindow`, con las fechas y el plazo por separado:
 * el inicio del torneo muestra solo las fechas (`range`; el plazo «juega
 * antes del domingo» ya no se muestra) y la jornada ya va en la píldora de
 * la tarjeta (Eduardo 2026-10-05, maqueta aprobada). */
export function nextMatchWindowParts(
  match: DashboardMatchBrief | null | undefined,
  stageLocked = false,
): { range: string; deadline: string } | null {
  if (!match || stageLocked || match.uses_court_calendar !== false) return null;
  if (match.status?.toLowerCase() === "bye") return null;
  return jornadaWindowParts(match.round_starts_on, match.round_ends_on);
}

function capitalize(text: string): string {
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;
}

/** «Del lunes 6 al domingo 12 de octubre». Sin «juega antes del domingo»
 * (Eduardo 2026-10-05: sobra, el rango ya termina ese día). */
function JornadaWindow({ parts, inline = false }: { parts: { range: string; deadline: string }; inline?: boolean }) {
  return (
    <p className={`pt-home__dates${inline ? " pt-home__dates--inline" : ""}`} data-no-toast data-jornada-window>
      <span>{capitalize(parts.range)}</span>
    </p>
  );
}

/** «Jornada 1 · del martes 6 al domingo 11 de octubre» (Eduardo 2026-10-05):
 * la jornada en la que descansa, sin «juega antes del…». Null sin fechas. */
export function restRoundLine(rest: PlayerDashboardRestRound | null | undefined): string | null {
  if (!rest?.round_starts_on || !rest.round_ends_on) return null;
  const range = jornadaRangeText(rest.round_starts_on, rest.round_ends_on);
  if (!range) return null;
  return [rest.round_label?.trim() || null, range].filter(Boolean).join(" · ");
}

function opponent(match: DashboardMatchBrief | null, profileId?: number | null, ownParticipantId?: number | null) {
  return retoRival(match, { profileId, ownParticipantIds: [ownParticipantId] });
}

function registrationFileUrl(path: string | null | undefined) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const appBase = API_BASE_URL.replace(/\/api\/v2$/, "").replace(/\/$/, "");
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${appBase}${normalized}`;
}

function playerDocumentPath(src: string, label: string, back: string) {
  const params = new URLSearchParams({ src, label, back });
  return `/player/documento?${params.toString()}`;
}

function isBye(match: DashboardMatchBrief | null, profileId?: number | null): boolean {
  if (!match) return false;
  const other = opponent(match, profileId);
  return !other || match.status?.toLowerCase() === "bye";
}

function matchTitle(match: DashboardMatchBrief | null, profileId?: number | null): string {
  if (!match) return "Sin reto publicado";
  if (isBye(match, profileId)) return "BYE · Descanso";
  const other = opponent(match, profileId);
  return other?.display_name ? `vs ${other.display_name}` : "Rival por definir";
}

function cleanGroupName(value?: string | null): string {
  if (!value) return "—";
  const number = value.match(/\d+/)?.[0];
  return number ?? value;
}

function statNumber(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : String(value);
}

/** Eduardo 2026-10-05: los números largos del resumen («134–128») bajan de
 * tamaño para caber en su columna en vez de partirse en dos renglones. */
function statClass(text: string, extra?: string): string | undefined {
  const classes = [text.length >= 6 ? "is-long" : null, extra ?? null].filter(Boolean);
  return classes.length > 0 ? classes.join(" ") : undefined;
}

/** Qué dice la nota del reto: un texto, el horario (con calendario de
 * canchas) o el contacto del rival («Coordina directo con tu rival»), que
 * el inicio del torneo pinta en su recuadro con el celular en grande. */
export type RetoNote = { kind: "contact" } | { kind: "schedule"; text: string } | { kind: "text"; text: string };

export function nextMatchNoteParts(
  match: DashboardMatchBrief | null,
  profileId?: number | null,
  stageLocked = false,
): RetoNote {
  if (!match && stageLocked) return { kind: "text", text: PLAYER_STAGE_NEXT_MATCH_MESSAGE };
  if (!match) return { kind: "text", text: "Cuando el admin publique tu siguiente juego, aparecerá aquí." };
  if (isBye(match, profileId)) {
    return { kind: "text", text: "Descansas porque fuiste el primer lugar de tu grupo, te lo mereces." };
  }
  // Publicar horarios (Eduardo 2026-10-01): con el horario aún sin publicar
  // el torneo SÍ usa calendario; no es «coordina con tu rival».
  if (isSchedulePendingForPlayer(match)) return { kind: "schedule", text: SCHEDULE_PENDING_LABEL };
  const schedule = [match.scheduled_date, match.scheduled_time, courtNoticeName(match.court) || null]
    .filter(Boolean)
    .join(" · ");
  if (schedule) return { kind: "schedule", text: schedule };
  // Sin calendario de canchas (Eduardo 2026-10-03) los jugadores coordinan
  // entre ellos: el celular del rival sale aunque capture el organizador.
  if (retoShowsContact(match)) return { kind: "contact" };
  return { kind: "text", text: `Consulta el perfil de tu rival. ${RESULT_BY_ORGANIZER_NOTE}` };
}

export function nextMatchNote(match: DashboardMatchBrief | null, profileId?: number | null, stageLocked = false): string {
  const note = nextMatchNoteParts(match, profileId, stageLocked);
  return note.kind === "contact" ? retoContactLine(opponent(match, profileId)) : note.text;
}

/** «Grupo 3» para la píldora del encabezado (el nombre ya puede traer
 * «Grupo»). */
function groupChipLabel(value: string): string {
  const clean = value.trim();
  return /^grupo\b/i.test(clean) ? clean : `Grupo ${clean}`;
}

function findTournament(
  data: PlayerDashboardPayload | null,
  tournamentId: number,
): PlayerDashboardTournamentCard | null {
  return data?.tournaments.find((card) => card.tournament_id === tournamentId) ?? null;
}

function findNextMatch(card: PlayerDashboardTournamentCard | null, data: PlayerDashboardPayload | null) {
  return (
    card?.next_match ??
    data?.upcoming_matches.find((match) => match.tournament_id === card?.tournament_id) ??
    null
  );
}

function summaryRow(standings: PlayerStandingsPayload | null) {
  return standings?.my_row ?? null;
}

// Eduardo 2026-10-05 (torneo 67): «RK Grupo» es el lugar del jugador DENTRO
// de su grupo (1..n, el mismo que muestra la tabla del grupo en Ranking),
// nunca su ranking general (current_rank, que va en «RK General»). Sin tabla
// de grupo (eliminación directa, «Sin grupo») queda en «—».
function groupRank(standings: PlayerStandingsPayload | null): number | null {
  const myId = standings?.my_participant_id;
  const group = participantGroupScope(standings?.groups, myId);
  if (!group || group.group_id == null) return null;
  return group.standings.find((row) => row.participant_id === myId)?.position ?? null;
}

// ----- Reto por jornada (Eduardo 2026-10-07) --------------------------------
// Round robin sin calendario de canchas: los jugadores adelantan jornadas. Un
// selector «Jornada 1 2 3…» en la tarjeta del reto muestra SU partido de la
// jornada elegida (rival, fechas, celular del rival y «Reportar resultado»),
// que descansa, o el resultado / su reporte de ese partido.

/** Jornada en la que abre el selector: la del descanso actual o la de su
 * próximo reto (la tarjeta de siempre). Null si su reto no es de la lista
 * (un playoff): ningún número queda marcado. */
export function defaultJornadaNumber(
  jornadas: PlayerJornadaReto[],
  nextMatch: DashboardMatchBrief | null,
  restRound: PlayerDashboardRestRound | null,
): number | null {
  if (restRound && jornadas.some((entry) => entry.round_number === restRound.round_number)) {
    return restRound.round_number;
  }
  if (!nextMatch) return null;
  return jornadas.find((entry) => entry.match?.id === nextMatch.id)?.round_number ?? null;
}

// El estado del partido de la jornada y su tarjeta viven en
// PlayerJornadaRetoCard (también la usa Juegos).
export { jornadaRetoState, type JornadaRetoState } from "../components/PlayerJornadaRetoCard";

// ----- H2H helpers ----------------------------------------------------------

function h2hLastResultLabel(result: "win" | "loss" | null): string {
  if (result === "win") return "Ganaste el último";
  if (result === "loss") return "Perdiste el último";
  return "Sin historial";
}

function h2hResultBadge(result: "win" | "loss" | "unknown"): string {
  if (result === "win") return "W";
  if (result === "loss") return "L";
  return "?";
}

function h2hResultBadgeClass(result: "win" | "loss" | "unknown"): string {
  if (result === "win") return "h2h-result--win";
  if (result === "loss") return "h2h-result--loss";
  return "h2h-result--unknown";
}

function formatH2HDate(dateStr: string | null): string {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0].slice(2)}`;
  return dateStr;
}

export default function PlayerTournamentHomePage() {
  const { id } = useParams();
  const tournamentId = Number(id);
  const { token } = useAuth();
  const [dashboard, setDashboard] = useState<PlayerDashboardPayload | null>(null);
  const [standings, setStandings] = useState<PlayerStandingsPayload | null>(null);
  const [h2h, setH2h] = useState<H2HPayload | null>(null);
  const [h2hLoading, setH2hLoading] = useState(false);
  const [h2hExpanded, setH2hExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentOrders, setPaymentOrders] = useState<PlayerPaymentOrder[]>([]);
  // Cuenta familiar (bug de Eduardo 2026-09-29): el home del torneo responde
  // por el perfil elegido — encabezado, credencial, próximo reto, resumen y
  // foto son de ESE hijo, no del primero de la cuenta.
  // La liga del aviso «Documentos pendientes» trae al hijo (`player_profile_id`).
  const location = useLocation();
  const navigate = useNavigate();
  const linkedProfileId = useMemo(() => {
    const raw = Number(new URLSearchParams(location.search).get("player_profile_id") ?? "");
    return Number.isFinite(raw) && raw > 0 ? raw : null;
  }, [location.search]);
  const scope = usePlayerProfileScope({ tournamentId, preferredProfileId: linkedProfileId });
  const scopeProfileId = scope.profileId;
  const { ready: scopeReady, profiles: scopeProfiles, setProfileId: setScopeProfileId } = scope;
  // Ese hijo se vuelve el perfil activo de la cuenta (igual que elegirlo en
  // «Jugando como») y el parámetro sale de la liga: así el resto del torneo
  // (Ranking, Juegos…) sigue con él y el selector vuelve a mandar. Sin lista
  // de la familia no se decide aquí: el servidor valida el perfil pedido.
  useEffect(() => {
    if (linkedProfileId === null || !scopeReady || scopeProfiles.length === 0) return;
    if (scopeProfiles.some((profile) => profile.id === linkedProfileId)) {
      setScopeProfileId(linkedProfileId);
    }
    const params = new URLSearchParams(location.search);
    params.delete("player_profile_id");
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash },
      { replace: true },
    );
  }, [linkedProfileId, location.hash, location.pathname, location.search, navigate, scopeProfiles, scopeReady, setScopeProfileId]);
  const latestLoad = useMemo(() => makeLatestOnly(), []);

  const load = useCallback(async () => {
    if (!token || !Number.isFinite(tournamentId) || !scope.ready) return;
    const attempt = latestLoad.begin();
    setLoading(true);
    setError(null);
    setPhotoError(null);
    try {
      const [dashboardResponse, standingsResponse] = await Promise.all([
        api.dashboards.player(token, scopeProfileId, { tournamentId }),
        api.standings.forPlayer(token, tournamentId, scopeProfileId),
      ]);
      if (!latestLoad.isCurrent(attempt)) return;
      setDashboard(dashboardResponse.data);
      setStandings(standingsResponse.data);
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el home del torneo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [latestLoad, scope.ready, scopeProfileId, token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let alive = true;
    if (!token) return;
    (async () => {
      try {
        const response = await api.dashboards.playerPaymentOrders(token);
        if (alive) setPaymentOrders(response.data.orders);
      } catch {
        // sin banner ante error: no bloquea el resto de la vista
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  // Perfil elegido: el pago pendiente que se avisa es el de ESE hijo (desde
  // que se elige, sin esperar a que llegue su home).
  const viewedProfileId = scopeProfileId ?? dashboard?.profile?.id ?? null;
  const pendingOrder = useMemo(
    () =>
      paymentOrders.find(
        (order) =>
          order.tournament_id === tournamentId &&
          (viewedProfileId == null || order.player_profile_id == null || order.player_profile_id === viewedProfileId),
      ) ?? null,
    [paymentOrders, tournamentId, viewedProfileId],
  );

  // Cuenta familiar: al cambiar de hijo, lo que sigue en pantalla es del
  // anterior hasta que llega el nuevo. Mientras tanto no se ofrece ninguna
  // acción que apunte a él (credencial, reportar, WhatsApp, foto).
  const shownProfileId = dashboard?.profile?.id ?? null;
  const switchingProfile = loading || (scopeProfileId != null && shownProfileId !== scopeProfileId);
  const shownProfileRef = useRef<number | null>(shownProfileId);
  useEffect(() => {
    shownProfileRef.current = shownProfileId;
  }, [shownProfileId]);

  // Foto del jugador (Eduardo 2026-09-10): subir desde el home del torneo y
  // decidir si los demás ven la foto de la credencial cuando no hay de perfil.
  // La acción es del hijo que se ve al empezar; si la pantalla ya cambió a
  // otro hijo cuando responde el servidor, no se pinta en el nuevo.
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  // Mientras sube un documento pendiente tampoco se cambia de hijo.
  const [documentsBusy, setDocumentsBusy] = useState(false);
  const handleUploadPhoto = useCallback(async (file: File) => {
    const targetProfileId = shownProfileRef.current;
    if (!token || targetProfileId === null) return;
    setPhotoBusy(true);
    setPhotoError(null);
    try {
      const response = await api.player.uploadAvatar(token, file, targetProfileId);
      const url = response.data.profile.avatar_url ?? null;
      setDashboard((prev) =>
        prev && prev.profile && prev.profile.id === targetProfileId
          ? { ...prev, profile: { ...prev.profile, avatar_url: url } }
          : prev,
      );
    } catch (err) {
      if (shownProfileRef.current !== targetProfileId) return;
      setPhotoError(err instanceof ApiError ? err.message || "No pudimos subir la foto." : "Error de red. Intenta otra vez.");
    } finally {
      setPhotoBusy(false);
    }
  }, [token]);
  const handlePhotoVisibility = useCallback(async (show: boolean) => {
    const targetProfileId = shownProfileRef.current;
    if (!token || targetProfileId === null) return;
    setPhotoBusy(true);
    setPhotoError(null);
    try {
      await api.player.setPhotoVisibility(token, show, targetProfileId);
      setDashboard((prev) =>
        prev && prev.profile && prev.profile.id === targetProfileId
          ? { ...prev, profile: { ...prev.profile, show_registration_photo: show } }
          : prev,
      );
    } catch (err) {
      if (shownProfileRef.current !== targetProfileId) return;
      setPhotoError(err instanceof ApiError ? err.message || "No pudimos guardar el cambio." : "Error de red. Intenta otra vez.");
    } finally {
      setPhotoBusy(false);
    }
  }, [token]);

  const card = useMemo(() => findTournament(dashboard, tournamentId), [dashboard, tournamentId]);
  // La credencial (foto, nombre y fecha de nacimiento) llega con liga firmada
  // de 1 h. Si el home se queda abierto, antes de que venza se piden los datos
  // otra vez SIN spinner y solo si siguen siendo del mismo hijo.
  const refreshCredentialLink = useCallback(async () => {
    if (!token || !Number.isFinite(tournamentId) || !scope.ready) return;
    const askedProfileId = shownProfileRef.current;
    const response = await api.dashboards.player(token, scopeProfileId, { tournamentId });
    const freshProfileId = response.data.profile?.id ?? null;
    setDashboard((current) =>
      current && freshProfileId === askedProfileId && shownProfileRef.current === askedProfileId ? response.data : current,
    );
  }, [scope.ready, scopeProfileId, token, tournamentId]);
  useSignedLinkRefresh([card?.credential_url], refreshCredentialLink, !loading);
  const nextMatch =useMemo(() => findNextMatch(card, dashboard), [card, dashboard]);
  const player = dashboard?.profile ?? null;
  // Candado del jugador: sin marcador, grupo ni lugar mientras se arma.
  const stageLocked = playerStageLocked(card?.player_visibility ?? standings?.player_visibility);
  const row: StandingsRow | null = stageLocked ? null : summaryRow(standings);
  const groupName = row?.group_name ?? nextMatch?.group?.name ?? null;
  const groupText = cleanGroupName(groupName);
  const tournamentName = card?.tournament_name ?? nextMatch?.tournament_name ?? `Torneo ${tournamentId}`;
  const record = row ? `${row.wins}–${row.losses}` : "—";
  const games = row ? `${row.games_won}–${row.games_lost}` : "—";
  const diff = row?.differential ?? null;
  const diffText = diff === null ? "—" : diff > 0 ? `+${diff}` : String(diff);
  const vocab = scoreVocab(standings?.sport);
  const other = opponent(nextMatch, player?.id, card?.participant_id);
  // Contacto y «Reportar resultado»: sin calendario de canchas siempre (el
  // reporte, solo si el torneo lo permite); con calendario, como antes.
  const showRetoActions =
    nextMatch !== null && !isBye(nextMatch, player?.id) && retoShowsContact(nextMatch) && !switchingProfile;
  // Descansa la jornada actual (Eduardo 2026-10-05): la tarjeta lo dice
  // primero y debajo, más discreto, su siguiente reto (rival, fechas,
  // contacto y «Reportar resultado»). El H2H sigue con ese rival. Un BYE ya
  // publicado (escalera) conserva su propia tarjeta de descanso.
  const restRound = stageLocked || isBye(nextMatch, player?.id) ? null : card?.rest_round ?? null;
  // La jornada ya va en la píldora: el renglón dice solo las fechas.
  const restRange =
    restRound?.round_starts_on && restRound.round_ends_on
      ? jornadaRangeText(restRound.round_starts_on, restRound.round_ends_on)
      : null;
  const restNext = restRound ? nextMatch : null;
  const restNextWindow = nextMatchWindowParts(restNext, stageLocked);
  const mainWindow = nextMatchWindowParts(nextMatch, stageLocked);
  // «Este torneo usa credencial» (Eduardo 2026-10-05, torneo 67): apagado, ni
  // el botón de la credencial ni la casilla de su foto. Sin el dato = sí.
  const credentialEnabled = card?.credential_enabled !== false;
  const credentialUrl = switchingProfile || !credentialEnabled ? null : registrationFileUrl(card?.credential_url);
  // Grupo de WhatsApp del torneo: solo llega si este perfil está inscrito
  // (y con su pago al corriente); mientras cambia de hijo no se ofrece.
  const whatsappGroups = switchingProfile ? [] : playerWhatsappGroups(card?.whatsapp_groups);
  const whatsappAfterPayment = !switchingProfile && Boolean(card?.whatsapp_groups_after_payment);
  const photoTools = Boolean(player) && !switchingProfile;
  // Botones del encabezado (maqueta aprobada): en celular, cuadrícula de
  // tantos como haya (3 con credencial, grupo y foto; 2 sin credencial).
  const actionCount = (credentialUrl ? 1 : 0) + (whatsappGroups.length > 0 ? 1 : 0) + (photoTools ? 1 : 0);
  // Píldoras: categoría (es pública, como en Jugadores), formato y grupo
  // (el grupo respeta el candado: sin él no hay fila ni reto).
  const categoryName = standings?.my_row?.division_name?.trim() || null;
  const chips = [
    categoryName,
    formatTournamentType(card?.tournament_type),
    groupName ? groupChipLabel(groupName) : null,
  ].filter((chip): chip is string => Boolean(chip));

  // ----- H2H fetch ----------------------------------------------------------
  useEffect(() => {
    if (!token || !other?.player_profile_id) {
      setH2h(null);
      return;
    }
    let cancelled = false;
    setH2hLoading(true);
    api.player.h2h(token, other.player_profile_id, player?.id)
      .then((res) => {
        if (!cancelled) setH2h(res.data);
      })
      .catch(() => {
        if (!cancelled) setH2h(null);
      })
      .finally(() => {
        if (!cancelled) setH2hLoading(false);
      });
    return () => { cancelled = true; };
  }, [token, other?.player_profile_id, player?.id]);

  // ----- Reto por jornada (Eduardo 2026-10-07) -------------------------------
  // El servidor solo manda jornadas en round robin sin calendario de canchas
  // (con canchas, escalera y eliminación la tarjeta queda igual). Ni con el
  // torneo en armado ni cerrado se pide.
  // Con la clave del perfil y torneo que los PIDIÓ (PR #581): dos perfiles de
  // la cuenta pueden compartir la pareja (mismo participant_id).
  const [jornadaRetos, setJornadaRetos] = useState<{ key: string; payload: PlayerJornadaRetosPayload } | null>(null);
  const [pickedJornada, setPickedJornada] = useState<number | null>(null);
  const tournamentClosed = card?.tournament_status === "closed" || card?.tournament_status === "archived";
  const wantsJornadas = Boolean(card) && !stageLocked && !tournamentClosed;
  useEffect(() => {
    setPickedJornada(null);
    if (!token || !wantsJornadas || !Number.isFinite(tournamentId)) {
      setJornadaRetos(null);
      return;
    }
    let cancelled = false;
    const requestedKey = `${tournamentId}:${scopeProfileId ?? "self"}`;
    api.standings
      .jornadaRetosForPlayer(token, tournamentId, scopeProfileId)
      .then((res) => {
        if (!cancelled) setJornadaRetos({ key: requestedKey, payload: res.data });
      })
      .catch(() => {
        if (!cancelled) setJornadaRetos(null);
      });
    return () => {
      cancelled = true;
    };
    // `dashboard`: se vuelve a pedir cada vez que llega el home (otro hijo).
  }, [token, tournamentId, scopeProfileId, wantsJornadas, dashboard]);
  // Cuenta familiar: solo las jornadas del perfil que se ve (pedidas para él).
  const ownRetos = jornadaRetos?.key === `${tournamentId}:${scopeProfileId ?? "self"}` ? jornadaRetos.payload : null;
  const jornadas =
    !switchingProfile &&
    ownRetos?.participant_id != null &&
    ownRetos.participant_id === card?.participant_id
      ? ownRetos.jornadas
      : [];
  const ownJornadaParticipantId = ownRetos?.participant_id ?? null;
  const showJornadaPicker = !loading && jornadas.length > 1;
  const defaultJornada = defaultJornadaNumber(jornadas, nextMatch, restRound);
  const pickedEntry =
    showJornadaPicker && pickedJornada !== null && pickedJornada !== defaultJornada
      ? jornadas.find((entry) => entry.round_number === pickedJornada) ?? null
      : null;
  const activeJornada = pickedEntry?.round_number ?? defaultJornada;


  // Rival del reto con liga a su perfil (en el principal y en el siguiente).
  const rivalTitle = (match: DashboardMatchBrief | null) =>
    other?.player_profile_id ? (
      <Link className="pt-home__rival-link" to={routes.playerGlobalProfile(other.player_profile_id)}>
        {matchTitle(match, player?.id)}
      </Link>
    ) : (
      matchTitle(match, player?.id)
    );

  // Nota y acciones del reto (Eduardo 2026-10-05, maqueta aprobada): sin
  // calendario de canchas, recuadro «Coordina directo con tu rival» con el
  // celular en grande, WhatsApp y Llamar lado a lado y «Reportar resultado»
  // a lo ancho; con calendario, el horario y las mismas acciones de antes.
  const renderRetoNote = (match: DashboardMatchBrief | null) => {
    const note = nextMatchNoteParts(match, player?.id, stageLocked);
    const actions =
      match && showRetoActions ? (
        <PlayerRetoActions
          variant="card"
          match={match}
          rival={other}
          sender={player}
          tournamentName={tournamentName}
          reportHref={retoReportHref(match.id, player?.id)}
          showContactLine={note.kind === "contact"}
          className="pt-home__reto-actions"
        />
      ) : null;
    if (note.kind === "contact") {
      return (
        actions ?? (
          <div className="player-reto-card__coord has-contact">
            <PlayerRetoContact rival={other} />
          </div>
        )
      );
    }
    return (
      <>
        {note.kind === "schedule" ? (
          <p className="pt-home__reto-when" data-no-toast>
            <Icon name="calendar" />
            {note.text}
          </p>
        ) : (
          <p className="pt-home__reto-note" data-no-toast>
            {note.text}
          </p>
        )}
        {actions}
      </>
    );
  };

  // Selector «Jornada 1 2 3…»: la jornada de la tarjeta de siempre queda
  // marcada; otra muestra su partido de esa jornada.
  const jornadaPicker = showJornadaPicker ? (
    <div className="pt-home__jornadas" role="group" aria-label="Elegir jornada">
      <span className="pt-home__jornadas-label">Jornada</span>
      <div className="pt-home__jornadas-chips">
        {jornadas.map((entry) => {
          const active = entry.round_number === activeJornada;
          return (
            <button
              key={entry.round_number}
              type="button"
              className={`pt-home__jornada-chip${active ? " is-active" : ""}${entry.rest ? " is-rest" : ""}`}
              aria-pressed={active}
              aria-label={entry.round_label}
              onClick={() => setPickedJornada(entry.round_number === defaultJornada ? null : entry.round_number)}
            >
              {entry.round_number}
            </button>
          );
        })}
      </div>
    </div>
  ) : null;

  // Su partido de la jornada elegida (o que descansa): rival, fechas,
  // celular del rival y «Reportar resultado», o su resultado/reporte.
  const renderPickedJornada = (entry: PlayerJornadaReto) => {
    const match = entry.match;
    const parts = jornadaWindowParts(entry.round_starts_on, entry.round_ends_on);
    if (entry.rest || !match) {
      return (
        <section className="pt-home__reto pt-home__reto--jornada" aria-labelledby="pt-home-reto-title">
          <div className="pt-home__reto-head">
            <span className="pt-home__eyebrow pt-home__eyebrow--rest">
              <Icon name="moon" />
              Esa jornada
            </span>
            <span className="pt-home__pill pt-home__pill--rest">{entry.round_label}</span>
          </div>
          {jornadaPicker}
          <div className="pt-home__reto-who">
            <h2 id="pt-home-reto-title" className="pt-home__rival">
              Descansas esta jornada
            </h2>
            {parts ? <JornadaWindow parts={parts} /> : null}
          </div>
          <p className="pt-home__reto-note" data-no-toast>
            No tienes partido en esta jornada. En los grupos con número impar de jugadores, cada jornada descansa uno.
          </p>
        </section>
      );
    }
    return (
      <PlayerJornadaRetoCard
        entry={entry}
        ownParticipantId={ownJornadaParticipantId}
        sender={player}
        playerProfileId={player?.id}
        tournamentName={tournamentName}
        headingId="pt-home-reto-title"
        picker={jornadaPicker}
      />
    );
  };

  const h2hHasHistory = Boolean(h2hExpanded && h2h && h2h.matches.length > 0);

  return (
    <section className="player-tournament-home pt-home" aria-label="Home del torneo jugador">
      {pendingOrder && (
        <section className="card pt-home__notice" role="status">
          <p>
            <strong>Tienes un pago de inscripción pendiente</strong> —{" "}
            {formatMoney(pendingOrder.gross_amount, pendingOrder.currency)}
          </p>
          <Link className="button" to={orderPaymentHref(pendingOrder)}>
            Pagar ahora
          </Link>
        </section>
      )}
      {error && (
        <section className="card pt-home__notice">
          <p className="form-error" role="alert">{error}</p>
        </section>
      )}

      <PlayerProfileScopeSwitcher scope={scope} tournament disabled={photoBusy || documentsBusy} />

      {/* ----- Encabezado: foto, nombre, torneo y sus botones ------------- */}
      <section className="pt-home__profile" aria-label="Tu perfil en el torneo">
        <div className="pt-home__who">
          <div
            className={`pt-home__avatar${player?.avatar_url ? " has-photo" : ""}`}
            aria-hidden={player?.avatar_url ? undefined : true}
          >
            {player?.avatar_url ? (
              <img src={player.avatar_url} alt={`Foto de ${player.display_name}`} />
            ) : (
              initials(player?.display_name ?? player?.first_name)
            )}
          </div>
          <div className="pt-home__who-copy">
            <h1>{player?.display_name ?? "Jugador"}</h1>
            <p className="pt-home__tournament">{loading ? "Cargando torneo…" : tournamentName}</p>
          </div>
          {/* Categoría, formato y grupo (antes «🏆 Torneo · Formato»). */}
          {!loading && chips.length > 0 ? (
            <div className="pt-home__chips">
              {chips.map((chip) => (
                <span key={chip} className="pt-home__chip">
                  {chip}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {actionCount > 0 || whatsappAfterPayment || photoTools || photoError ? (
          <div className="pt-home__tools">
            {actionCount > 0 ? (
              <div className={`pt-home__actions pt-home__actions--count-${actionCount}`} data-count={actionCount}>
                {credentialUrl ? (
                  <Link
                    className="pt-home__action"
                    to={playerDocumentPath(credentialUrl, "Credencial", `/player/tournaments/${tournamentId}`)}
                  >
                    <Icon name="id-card" />
                    Ver / compartir credencial
                  </Link>
                ) : null}
                {/* Eduardo 2026-10-01: el grupo de WhatsApp que el organizador
                    puso en Datos; con varios, una lista para elegir. */}
                <PlayerWhatsappGroupsButton
                  groups={whatsappGroups}
                  className="pt-home__action pt-home__action--whatsapp"
                  lineIcon
                />
                {player && photoTools ? (
                  <div className="pt-home__action-wrap">
                    <label className={`pt-home__action pt-home__action--photo${photoBusy ? " is-disabled" : ""}`}>
                      <Icon name="camera" />
                      {/* «de perfil»: no es la «Foto de medio cuerpo» que pide el
                          organizador (esa se sube en la tarjeta de documentos). */}
                      {photoBusy ? "Guardando…" : player.avatar_url ? "Cambiar foto de perfil" : "Subir foto de perfil"}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        disabled={photoBusy}
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          event.target.value = "";
                          if (file) void handleUploadPhoto(file);
                        }}
                      />
                    </label>
                    <FieldHint label="Cómo funciona la foto de perfil">
                      Elige una imagen JPG, PNG o WebP de hasta 5 MB. Se convierte en tu foto de perfil en todos tus torneos y la ven los demás
                      jugadores junto a tu nombre en Ranking y Jugadores. Puedes cambiarla cuando quieras desde aquí o desde Mi perfil.
                      {credentialEnabled
                        ? " No es la foto para la credencial que pide el organizador: esa se sube en «Documentos pendientes»."
                        : null}
                    </FieldHint>
                  </div>
                ) : null}
              </div>
            ) : null}
            {whatsappAfterPayment ? (
              <p className="pt-home__note">El grupo de WhatsApp del torneo se te compartirá al confirmar tu pago.</p>
            ) : null}
            {player && photoTools && credentialEnabled ? (
              <div className="pt-home__photo-switch">
                <label>
                  <input
                    type="checkbox"
                    checked={player.show_registration_photo ?? true}
                    disabled={photoBusy}
                    onChange={(event) => void handlePhotoVisibility(event.target.checked)}
                  />
                  <span>Mostrar la foto de mi credencial</span>
                </label>
                <FieldHint label="Qué hace Mostrar la foto de mi credencial">
                  Si no has subido foto de perfil, los demás jugadores del torneo ven junto a tu nombre la foto que mandaste al inscribirte
                  (la de tu credencial). Apágalo y verán solo tus iniciales. La credencial no cambia y nadie fuera del torneo ve tu foto.
                </FieldHint>
              </div>
            ) : null}
            {photoError ? (
              <p className="form-error pt-home__photo-error" role="alert">
                {photoError}
              </p>
            ) : null}
          </div>
        ) : null}
      </section>

      {/* Secciones del torneo: en computadora, pestañas aquí arriba; en
          celular, la barra flotante de abajo. La barra usa la tarjeta del
          torneo: no pinta «Credencial» antes de saber. */}
      <PlayerTournamentBottomNav
        tournamentId={tournamentId}
        credentialEnabled={loading ? null : card ? credentialEnabled : undefined}
      />

      {/* Documentos de la inscripción que el organizador todavía espera
          (foto, CURP): se suben aquí, dentro del torneo que los pide. El
          candado del jugador no los esconde. */}
      <PlayerPendingDocumentsCard
        tournamentId={tournamentId}
        profileId={switchingProfile ? null : shownProfileId}
        onBusyChange={setDocumentsBusy}
      />

      <div className="pt-home__grid">
        <div className="pt-home__col pt-home__col--main">
          {pickedEntry ? (
            renderPickedJornada(pickedEntry)
          ) : !loading && restRound ? (
            /* Descansa la jornada actual: primero el descanso y debajo,
               en una tarjeta más chica, su siguiente reto. */
            <section className="pt-home__rest" aria-labelledby="pt-home-rest-title">
              <div className="pt-home__rest-top">
                <div className="pt-home__reto-head">
                  <span className="pt-home__eyebrow pt-home__eyebrow--rest">
                    <Icon name="moon" />
                    Esta jornada
                  </span>
                  <span className="pt-home__pill pt-home__pill--rest">{restRound.round_label}</span>
                </div>
                {jornadaPicker}
                <h2 id="pt-home-rest-title" className="pt-home__rest-title">
                  Descansas
                </h2>
                {restRange ? (
                  <p className="pt-home__rest-dates" data-no-toast data-jornada-window>
                    {capitalize(restRange)}
                  </p>
                ) : null}
                <p className="pt-home__rest-copy" data-no-toast>
                  No tienes partido en esta jornada. En los grupos con número impar de jugadores, cada jornada descansa uno.
                </p>
              </div>
              <div className="pt-home__rest-next">
                {restNext ? (
                  <div className="pt-home__reto pt-home__reto--next">
                    <div className="pt-home__reto-head">
                      <span className="pt-home__eyebrow">Tu siguiente reto</span>
                      <span className="pt-home__pill">{roundBadge(restNext)}</span>
                    </div>
                    <div className="pt-home__reto-who">
                      <h3 className="pt-home__rival">{rivalTitle(restNext)}</h3>
                      {restNextWindow ? <JornadaWindow parts={restNextWindow} inline /> : null}
                    </div>
                    {renderRetoNote(restNext)}
                  </div>
                ) : (
                  <p className="pt-home__rest-empty" data-no-toast>
                    Tu siguiente reto aparecerá aquí cuando el organizador lo publique.
                  </p>
                )}
              </div>
            </section>
          ) : (
            <section className="pt-home__reto" aria-labelledby="pt-home-reto-title">
              <div className="pt-home__reto-head">
                <span className="pt-home__eyebrow">Tu próximo reto</span>
                <span className="pt-home__pill">{roundBadge(nextMatch)}</span>
              </div>
              {jornadaPicker}
              <div className="pt-home__reto-who">
                <h2 id="pt-home-reto-title" className="pt-home__rival">
                  {loading ? "Cargando…" : rivalTitle(nextMatch)}
                </h2>
                {!loading && mainWindow ? <JornadaWindow parts={mainWindow} /> : null}
              </div>
              {loading ? (
                <p className="pt-home__reto-note" data-no-toast>
                  Buscando tu siguiente reto…
                </p>
              ) : (
                renderRetoNote(nextMatch)
              )}
            </section>
          )}

          {/* ----- Head to Head (renglón plegable) ---------------------------- */}
          {other?.player_profile_id && !isBye(nextMatch, player?.id) && (
            <article className="h2h-panel" aria-label="Head to Head">
              <button
                className="h2h-panel__toggle"
                type="button"
                onClick={() => setH2hExpanded((v) => !v)}
                aria-expanded={h2hExpanded}
                aria-controls={h2hHasHistory ? "pt-home-h2h-history" : undefined}
              >
                <span className="h2h-panel__badge" aria-hidden="true">
                  VS
                </span>
                <span className="h2h-panel__text">
                  <span className="h2h-panel__toggle-label">Head to Head vs {other.display_name ?? "Rival"}</span>
                  <span className="h2h-panel__summary">
                    {h2hLoading ? (
                      <span className="h2h-panel__loading">Cargando historial…</span>
                    ) : h2h && h2h.summary.total_matches > 0 ? (
                      <>
                        <span className="h2h-panel__record">
                          <strong>{h2h.summary.player_wins}W</strong> – <strong>{h2h.summary.opponent_wins}L</strong>
                        </span>
                        {h2h.summary.tournament_wins + h2h.summary.tournament_losses > 0 && (
                          <span className="h2h-panel__source-badge h2h-panel__source-badge--torneo">
                            Torneo {h2h.summary.tournament_wins}-{h2h.summary.tournament_losses}
                          </span>
                        )}
                        {h2h.summary.friendly_wins + h2h.summary.friendly_losses > 0 && (
                          <span className="h2h-panel__source-badge h2h-panel__source-badge--amistoso">
                            Amistoso {h2h.summary.friendly_wins}-{h2h.summary.friendly_losses}
                          </span>
                        )}
                        <span className="h2h-panel__last">· {h2hLastResultLabel(h2h.summary.last_result)}</span>
                        {h2h.summary.last_score && (
                          <span className="h2h-panel__last-score">· {h2h.summary.last_score}</span>
                        )}
                      </>
                    ) : (
                      <span className="h2h-panel__empty">Primer enfrentamiento — ¡a estrenar el H2H!</span>
                    )}
                  </span>
                </span>
                <Icon name="chevron-down" className={`h2h-panel__toggle-arrow${h2hExpanded ? " is-open" : ""}`} />
              </button>

              {h2hHasHistory && h2h ? (
                <div id="pt-home-h2h-history" className="h2h-panel__history">
                  <table className="h2h-panel__table">
                    <thead>
                      <tr>
                        <th>R</th>
                        <th>Score</th>
                        <th>Tipo</th>
                        <th>Torneo / Rival</th>
                        <th>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {h2h.matches.map((m) => (
                        <tr key={m.id}>
                          <td>
                            <span className={`h2h-result ${h2hResultBadgeClass(m.result)}`}>
                              {h2hResultBadge(m.result)}
                            </span>
                          </td>
                          <td className="h2h-panel__score">{m.score_text ?? "—"}</td>
                          <td>
                            <span
                              className={`h2h-panel__source-badge ${
                                m.match_source === "friendly"
                                  ? "h2h-panel__source-badge--amistoso"
                                  : "h2h-panel__source-badge--torneo"
                              }`}
                            >
                              {m.match_source === "friendly" ? "Amistoso" : "Torneo"}
                            </span>
                          </td>
                          <td className="h2h-panel__tournament">
                            {m.match_source === "friendly"
                              ? `vs ${m.opponent.display_name}`
                              : (m.tournament_name ?? `Torneo ${m.tournament_id}`)}
                          </td>
                          <td className="h2h-panel__date">{formatH2HDate(m.scheduled_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </article>
          )}

          {/* ----- Amigos (renglón plegable) ------------------------------------ */}
          <div className="pt-home__amigos">
            <AmigosPanel
              myProfileId={player?.id}
              opponentProfileId={other?.player_profile_id ?? null}
              isBye={isBye(nextMatch, player?.id)}
            />
          </div>
        </div>

        <div className="pt-home__col pt-home__col--side">
          <section className="pt-home__summary" aria-labelledby="pt-home-summary-title">
            <h2 id="pt-home-summary-title">Tu resumen del torneo</h2>
            <dl className="pt-home__stats">
              <div className="is-key">
                <dt>Grupo</dt>
                <dd className={statClass(groupText)}>{groupText}</dd>
              </div>
              <div className="is-key">
                <dt>RK Grupo</dt>
                <dd>{stageLocked ? "—" : statNumber(groupRank(standings))}</dd>
              </div>
              <div className="is-key">
                <dt>RK General</dt>
                <dd>{stageLocked ? "—" : statNumber(standings?.my_position ?? card?.current_rank ?? card?.tournament_rank)}</dd>
              </div>
              <div>
                <dt>Récord</dt>
                <dd className={statClass(record)}>{record}</dd>
              </div>
              <div>
                <dt>{vocab.ggHeader}–{vocab.gpHeader}</dt>
                <dd className={statClass(games)}>{games}</dd>
              </div>
              <div>
                <dt>Dif</dt>
                <dd className={statClass(diffText, diff !== null && diff >= 0 ? "is-positive" : undefined)}>{diffText}</dd>
              </div>
            </dl>
          </section>

          <TournamentSponsorSlots layout="home" className="pt-home__media" />
        </div>
      </div>
    </section>
  );
}
